import type { GhmSignalKind } from "@ghm/mqtt-contract";

import { normalizeMode } from "@/features/devices/adapters";
import type { SignalType } from "@/features/machines/types";
import type { WidgetType } from "@/features/widgets/types";

/**
 * Catálogo de widgets dirigido por meta (P1.1 / P1.2) — o motor de coerência
 * widget↔sinal. Para cada modo de função que o hardware declara no `/meta`,
 * define o CONJUNTO CURADO de widgets que aparece por padrão e o tipo visual
 * coerente com a semântica de cada sinal:
 *
 *   - taxa instantânea (`v`)  → gauge   (velocidade / vazão / taxa)
 *   - totalizador (`acc`)     → number  (distância / total / transições)
 *   - estado (`s`)            → led     (estado / sentido / latch)
 *
 * Substitui a escolha rasa anterior (1 widget por função, no sinal
 * primário). Agora um encoder conectado mostra velocidade + distância +
 * sentido automaticamente, sem o operador configurar nada.
 *
 * A ordem dentro de cada modo define a ordem dos cards no grid.
 */

export interface CatalogEntry {
  signal: SignalType;
  widgetType: WidgetType;
}

const GAUGE = (signal: SignalType): CatalogEntry => ({ signal, widgetType: "gauge" });
const NUMBER = (signal: SignalType): CatalogEntry => ({ signal, widgetType: "number" });
const LED = (signal: SignalType): CatalogEntry => ({ signal, widgetType: "led" });

const WIDGET_CATALOG: Record<string, CatalogEntry[]> = {
  // Encoder: velocidade (gauge) + distância acumulada (number) + sentido (LED).
  HSC: [GAUGE("v"), NUMBER("acc"), LED("s")],
  // Contador: taxa (gauge) + total (number) + estado (LED).
  CT: [GAUGE("v"), NUMBER("acc"), LED("s")],
  // Medidor de pulso: vazão (gauge) + total (number).
  PM: [GAUGE("v"), NUMBER("acc")],
  // Entrada digital: estado (LED) + transições acumuladas (number).
  DI: [LED("s"), NUMBER("acc")],
  // Latch set/reset: estado do latch (LED) + transições (number).
  LATCH: [LED("s"), NUMBER("acc")],
  // Fonte de reset: acionamentos acumulados (number).
  RST: [NUMBER("acc")],
  // Leitura analógica escalada para engenharia: valor instantâneo (gauge) e,
  // QUANDO o bloco totaliza (vazão → volume), o total acumulado (number).
  // O `acc` só materializa se o firmware declarar em `values` — degradação
  // honesta: sem totalizador, nada de card fantasma. O `s` do payload carrega
  // o status NAMUR NE43 (0=falha, 1=abaixo, 2=normal, 3=acima), tratado pela
  // camada de qualidade — não é um sinal para o operador ver.
  AIN: [GAUGE("v"), NUMBER("acc")],
  // Modbus e um protocolo, nao uma grandeza de processo. O operador escolhe
  // conscientemente entre valor e total conforme o mapa do equipamento; o
  // status de comunicacao aparece como diagnostico, nunca como LED binario.
  MB: [],
};

// Fallback para modo desconhecido: oferece os três sinais com o tipo
// coerente; o filtro por sinais declarados/presentes decide o que entra.
const UNKNOWN_CATALOG: CatalogEntry[] = [GAUGE("v"), NUMBER("acc"), LED("s")];

/** Entradas do catálogo para um modo de função (normalizado). */
export function catalogEntriesForMode(mode: unknown): CatalogEntry[] {
  return WIDGET_CATALOG[normalizeMode(mode)] ?? UNKNOWN_CATALOG;
}

/**
 * Widget coerente com a SEMÂNTICA declarada, quando ela diverge do que o modo
 * sugere. Um `gauge` sobre um acumulado nativo (o `kWh total` lido do medidor)
 * é um ponteiro que nunca volta: sobe até o fundo de escala e fica lá. O
 * número grande é a leitura honesta de algo que só cresce.
 *
 * `state` (código de falha, modo de operação) também é número: média e
 * tendência de uma enumeração não significam nada.
 */
export function widgetTypeForKind(kind: GhmSignalKind): WidgetType {
  return kind === "cumulative" || kind === "state" ? "number" : "gauge";
}

/** Modos em que `s` é DIAGNÓSTICO multi-estado, não binário de processo. */
const DIAGNOSTIC_STATE_MODES = new Set(["AIN", "MB"]);

/**
 * O `s` só é um sinal PARA O OPERADOR quando é binário de processo (nível de
 * uma DI, estado de uma latch, sentido de um encoder). Em `AIN` ele carrega o
 * status NAMUR NE43 (0=falha, 1=abaixo, 2=normal, 3=acima) e em `MB` o status
 * da transação (0=nunca leu, 1=ok, 2=timeout, 3=quadro inválido, 4=exceção).
 *
 * Oferecer esses dois como fonte de widget produz leitura FALSA, não apenas
 * feia. Um LED com o `activeWhen: 1` padrão acende em `s=1` e apaga em `s=2`
 * (timeout) e `s=4` (exceção): um escravo mudo aparece na tela como
 * equipamento desligado, que é outra coisa. Disponibilidade/status de produção
 * sobre o mesmo campo passam a medir "% do tempo em que o barramento
 * respondeu" com cara de indicador de processo.
 *
 * O diagnóstico continua chegando ao operador — pelo `diagnostic` resolvido em
 * `selectors.ts`, que os cards de valor já apresentam com texto próprio
 * ("Sem resposta do equipamento Modbus", "Falha no sinal analógico").
 *
 * Regra registrada em `docs/decisoes-ativas.md`.
 */
export function isOperatorSelectableSignal(mode: unknown, signal: SignalType): boolean {
  if (signal !== "s") return true;
  return !DIAGNOSTIC_STATE_MODES.has(normalizeMode(mode));
}
